import {useContext, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {useNavigate} from "react-router-dom";
import {useErrorTranslation} from "../../../../api/utilities/translateErrorMessage.js";
import useAuthorityConfiguration from "../../../../api/utilities/useAuthorityConfiguration.js";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import toast from "react-hot-toast";
import useProjectDomains from "../../../../api/utilities/useProjectDomains.js";
import Creatable from "react-select/creatable";
import Spinner from "../../../../components/Spinner.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrashCan} from "@fortawesome/free-regular-svg-icons";
import {IntegrationActions, IntegrationAddButton, IntegrationSection} from "../IntegrationDetailUi.jsx";
import Button from "../../../../components/Button.jsx";
import StoredSecretInput from "../IntegrationFormInput/StoredSecretInput.jsx";

export default function HeurekaIntegrationForm({project, projectIntegration}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let navigate = useNavigate();

    let {resetCurrentUserProjects} = useContext(GlobalContext);

    let authorityConfiguration = useAuthorityConfiguration();
    let projectDomains = useProjectDomains(project);

    let [heurekaSettings, setHeurekaSettings] = useState({
        hostNameAndApiKeyList: projectIntegration?.hostNameAndApiKeyList?.length
            ? projectIntegration.hostNameAndApiKeyList
            : [{hostName: '', apiKey: ''}],
    });

    let hostNameAndApiKeyList = heurekaSettings.hostNameAndApiKeyList ?? [];
    let isSubmitDisabled = hostNameAndApiKeyList.length === 0 || hostNameAndApiKeyList.some((row) => {
        return !row.hostName || (!row.apiKeyConfigured && !row.apiKey?.trim());
    });

    let hostNameOptions = useMemo(() => {
        if (projectDomains === null) {
            return [];
        }

        return projectDomains.map((projectDomain) => ({
            value: projectDomain.projectDomain.hostName,
            label: projectDomain.projectDomain.hostName,
        }));
    }, [projectDomains]);

    let createHeurekaIntegration = () => {
        new ProjectIntegrationsApi(authorityConfiguration)
            .heurekaIntegration({
                projectId: project.id,
                integrationId: projectIntegration?.id ?? null,
                heurekaIntegrationInput: heurekaSettings,
            })
            .then(heurekaIntegrationData => {
                navigate(
                    '/project/' + project.id + '/settings/integrations?integrationId=' + heurekaIntegrationData.id
                );
                resetCurrentUserProjects();
                toast.success(projectIntegration === null ? t('created') : t('saved'));
            })
            .catch(e => et(e).then((message) => { if (message) toast.error(message); }));
    };

    let updateCredentialsRow = (index, row) => {
        setHeurekaSettings((heurekaSettings) => {
            let hostNameAndApiKeyList = [...heurekaSettings.hostNameAndApiKeyList];
            hostNameAndApiKeyList[index] = row;

            return {...heurekaSettings, hostNameAndApiKeyList};
        });
    };

    let deleteCredentialsRow = (index) => {
        setHeurekaSettings((heurekaSettings) => {
            let hostNameAndApiKeyList = heurekaSettings.hostNameAndApiKeyList
                .filter((row, checkedIndex) => checkedIndex !== index);

            return {
                ...heurekaSettings,
                hostNameAndApiKeyList: hostNameAndApiKeyList.length ? hostNameAndApiKeyList : [{hostName: '', apiKey: ''}],
            };
        });
    };

    let addCredentialsRow = () => {
        setHeurekaSettings((heurekaSettings) => ({
            ...heurekaSettings,
            hostNameAndApiKeyList: [
                ...heurekaSettings.hostNameAndApiKeyList,
                {hostName: '', apiKey: ''},
            ],
        }));
    };


    // noinspection JSUnresolvedReference
    return (
        <IntegrationSection
            title={'Domény a API klíče'}
            description={'Každá doména používá vlastní Heureka API klíč a stahuje se nezávisle.'}
        >

            {projectDomains === null && <Spinner/>}
            {projectDomains && <div className={'flex flex-col gap-3'}>
                <div className={'flex flex-col gap-3'}>
                    {hostNameAndApiKeyList.map((row, index) => (
                        <div className={'rounded-xl border border-[#ddd] bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40'} key={'heureka-api-key-' + index}>
                            <div className={'flex items-start justify-between gap-3'}>
                                <div className={'text-sm font-semibold text-slate-700'}>
                                    Doména a API klíč #{index + 1}
                                </div>
                                <button
                                    type={'button'}
                                    className={'flex h-8 w-8 items-center justify-center rounded-md border border-red-500 bg-red-500 text-sm text-white shadow-sm hover:border-red-600 hover:bg-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2'}
                                    title={'Odebrat doménu'}
                                    onClick={() => deleteCredentialsRow(index)}
                                >
                                    <FontAwesomeIcon icon={faTrashCan}/>
                                </button>
                            </div>

                            <div className={'mt-3 flex flex-col gap-4'}>
                                <div className={'flex flex-col gap-1.5'}>
                                    <label className={'text-sm font-medium text-slate-700'}>
                                    Doména e-shopu
                                    </label>
                                    <Creatable
                                        classNamePrefix={'select'}
                                        value={row.hostName ? {
                                            label: row.hostName,
                                            value: row.hostName,
                                        } : null}
                                        onChange={(option) => {
                                            updateCredentialsRow(index, {...row, hostName: option?.value ?? ''});
                                        }}
                                        options={hostNameOptions}
                                        placeholder={'Vyberte nebo zadejte doménu'}
                                    />
                                </div>

                                <div className={'flex flex-col gap-1.5'}>
                                    <label className={'text-sm font-medium text-slate-700'}>
                                    Heureka API klíč
                                    </label>
                                    <StoredSecretInput
                                        configured={row.apiKeyConfigured ?? false}
                                        value={row.apiKey ?? ''}
                                        placeholder={'Vložte API klíč pro reporty konverzí'}
                                        onChange={(apiKey) => {
                                            updateCredentialsRow(index, {...row, apiKey});
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className={'flex flex-col gap-3'}>
                    <div className={'flex justify-center'}>
                        <IntegrationAddButton onClick={addCredentialsRow}>Přidat doménu</IntegrationAddButton>
                    </div>
                </div>
            </div>}

            <div className={'mt-5'}>
            <IntegrationActions hint={isSubmitDisabled ? 'Pro uložení vyplňte v každém řádku doménu i API klíč.' : null}>
                <Button
                    className={'button-primary'}
                    onClick={createHeurekaIntegration}
                    title={projectIntegration ? 'Uložit změny' : 'Vytvořit integraci'}
                    disabled={isSubmitDisabled}
                >
                    {projectIntegration ? 'Uložit změny' : 'Vytvořit integraci'}
                </Button>
            </IntegrationActions>
            </div>
        </IntegrationSection>
    );
}
